import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Polyline, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import API from "../../Network/API";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// const MAP_TILE =
//     "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const MAP_TILE =
    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
/** Padded bbox (~Bangladesh); limits panning + initial framing */
const BD_BOUNDS = L.latLngBounds([20.34, 87.62], [26.82, 93.12]);
const BD_CENTER = [24.165843, 90.549038];

/** `false` = প্রতিটা সাইট আলাদা মার্কার। `true` করলে আবার leaflet.markercluster চালু। */
const USE_MAP_MARKER_CLUSTER = true;

/*
 * ─── CLUSTER BACKUP (যখন USE_MAP_MARKER_CLUSTER = true) ─────────────────────────
 * const group = L.markerClusterGroup({
 *     showCoverageOnHover: false,
 *     maxClusterRadius: 52,
 *     spiderfyOnMaxZoom: true,
 *     chunkedLoading: true,
 * });
 * ─── এর পরিবর্তে নিচে branch এ layerGroup ব্যবহার হয় ────────────────────────────
 */

function mapContainerConnected(map) {
    try {
        const el = map?.getContainer?.();
        return Boolean(el?.isConnected);
    } catch {
        return false;
    }
}

function escapeHtml(s) {
    if (s == null) return "";
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function boundsNearlyEqual(a, b, eps = 2e-5) {
    if (!a || !b) return false;
    return (
        Math.abs(a.north - b.north) < eps &&
        Math.abs(a.south - b.south) < eps &&
        Math.abs(a.east - b.east) < eps &&
        Math.abs(a.west - b.west) < eps
    );
}

function sitesListFingerprint(list) {
    if (!list?.length) return "";
    return [...list].map((s) => String(s.id)).sort().join("|");
}

/** Matches legacy ajax_map / html_status: active alarm → warn tile. */
function popupAlarmCell(alarmActive) {
    const cls = alarmActive
        ? "map-popup-status map-popup-status--warn"
        : "map-popup-status map-popup-status--ok";
    const label = alarmActive ? "Alarm" : "Normal";
    return `<span class="${cls}" title="${label}" aria-label="${label}"></span>`;
}

function buildSitePopupHtml(site) {
    const siteId = escapeHtml(site.siteCode || String(site.id ?? ""));
    const zone = escapeHtml(site.zoneLabel || "—");
    const addr = escapeHtml(site.popupAddress || "—");
    const ll = `${escapeHtml(String(site.lat ?? ""))},${escapeHtml(String(site.lng ?? ""))}`;
    const tenants = Array.isArray(site.alarmTenants) ? site.alarmTenants : [];

    const metrics = [
        { field: "door_open", label: "Door Open" },
        { field: "mains_fail", label: "Mains Fail" },
        { field: "dc_low", label: "Dc Low" },
        { field: "site_down", label: "Site Down" },
    ];

    let alarmBlock = "";
    if (!tenants.length) {
        alarmBlock =
            '<table class="map-popup-inner"><tbody><tr><td class="map-popup-inner-empty" colspan="2">No RMS tenant data</td></tr></tbody></table>';
    } else {
        const headCells = tenants
            .map((t) => `<th class="map-popup-inner-th">${escapeHtml(t.tenant_name ?? "—")}</th>`)
            .join("");
        const rows = metrics
            .map((m) => {
                const cells = tenants
                    .map((t) => `<td class="map-popup-inner-td">${popupAlarmCell(Boolean(t[m.field]))}</td>`)
                    .join("");
                return `<tr><td class="map-popup-inner-label">${escapeHtml(m.label)}</td>${cells}</tr>`;
            })
            .join("");
        alarmBlock =
            '<table class="map-popup-inner"><thead><tr><th class="map-popup-inner-th map-popup-inner-corner">Alarm Name</th>' +
            headCells +
            "</tr></thead><tbody>" +
            rows +
            "</tbody></table>";
    }

    return (
        '<div class="map-popup-custom">' +
        '<table class="map-popup-outer">' +
        '<thead><tr><th colspan="2" class="map-popup-title">SITE ID: ' +
        siteId +
        "</th></tr></thead><tbody>" +
        '<tr><td class="map-popup-k">Zone:</td><td class="map-popup-v">' +
        zone +
        "</td></tr>" +
        '<tr><td class="map-popup-k">Address:</td><td class="map-popup-v">' +
        addr +
        "</td></tr>" +
        '<tr><td class="map-popup-k">Lat&amp;Lang:</td><td class="map-popup-v">' +
        ll +
        "</td></tr>" +
        '<tr><td colspan="2" class="map-popup-banner">Alarm Statistics</td></tr>' +
        '<tr><td colspan="2" class="map-popup-alarm-wrap">' +
        alarmBlock +
        "</td></tr>" +
        "</tbody></table></div>"
    );
}

function extractSiteList(body) {
    if (body == null) return [];
    if (Array.isArray(body)) return body;
    if (body.success === true && body.data != null) {
        const d = body.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d.data)) return d.data;
    }
    const nested = body?.data?.data ?? body?.data;
    if (Array.isArray(nested)) return nested;
    if (Array.isArray(body?.data)) return body.data;
    return body?.sites ?? body?.rows ?? [];
}

function deriveClusterTitle(sites) {
    const labels = [...new Set(sites.map((s) => s.clusterLabel).filter(Boolean))];
    if (labels.length === 1) return labels[0];
    if (labels.length === 0) return "All sites";
    return `${labels[0]} · +${labels.length - 1}`;
}

function edgeKey(a, b) {
    const x = String(a.id);
    const y = String(b.id);
    return x < y ? `${x}:${y}` : `${y}:${x}`;
}

/** Same cluster: chain by (lat,lng). Adjacent clusters: one bridge (nearest pair). */
function buildRouteSegments(sites) {
    if (!sites?.length || sites.length < 2) return [];

    const byCluster = new Map();
    for (const s of sites) {
        const k = String(s.clusterKey ?? "default");
        if (!byCluster.has(k)) byCluster.set(k, []);
        byCluster.get(k).push(s);
    }

    const segments = [];
    const seen = new Set();

    for (const [, group] of byCluster) {
        if (group.length < 2) continue;
        const sorted = [...group].sort((a, b) => a.lat - b.lat || a.lng - b.lng);
        for (let i = 0; i < sorted.length - 1; i++) {
            const a = sorted[i];
            const b = sorted[i + 1];
            const ek = edgeKey(a, b);
            if (seen.has(ek)) continue;
            seen.add(ek);
            segments.push({ a, b });
        }
    }

    const clusterEntries = [...byCluster.entries()].filter(([, g]) => g.length > 0);
    if (clusterEntries.length < 2) return segments;

    const meta = clusterEntries.map(([key, group]) => {
        let slat = 0;
        let slng = 0;
        for (const s of group) {
            slat += s.lat;
            slng += s.lng;
        }
        const n = group.length;
        return { key, group, clat: slat / n, clng: slng / n };
    });
    meta.sort((x, y) => x.clat - y.clat || x.clng - y.clng);

    for (let i = 0; i < meta.length - 1; i++) {
        const g1 = meta[i].group;
        const g2 = meta[i + 1].group;
        let best = null;
        let bestD = Infinity;
        for (const p of g1) {
            for (const q of g2) {
                const d = (p.lat - q.lat) ** 2 + (p.lng - q.lng) ** 2;
                if (d < bestD) {
                    bestD = d;
                    best = [p, q];
                }
            }
        }
        if (best) {
            const ek = edgeKey(best[0], best[1]);
            if (!seen.has(ek)) {
                seen.add(ek);
                segments.push({ a: best[0], b: best[1] });
            }
        }
    }

    return segments;
}

const ROUTE_LINE = {
    color: "#38bdf8",
    weight: 2.5,
    opacity: 0.95,
    dashArray: "10 14",
    lineCap: "round",
    lineJoin: "round",
};

function MapBoundsListener({ onBoundsCommit }) {
    const map = useMap();
    const timerRef = useRef(null);

    const commit = useCallback(() => {
        try {
            if (!mapContainerConnected(map)) return;
            const b = map.getBounds();
            onBoundsCommit({
                north: b.getNorth(),
                south: b.getSouth(),
                east: b.getEast(),
                west: b.getWest(),
            });
        } catch {
            /* map unmounting */
        }
    }, [map, onBoundsCommit]);

    useEffect(() => {
        let alive = true;

        const schedule = () => {
            if (!alive || !mapContainerConnected(map)) return;
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => {
                if (alive) commit();
            }, 380);
        };

        const immediateOnce = () => {
            if (alive) commit();
        };

        map.whenReady(immediateOnce);
        map.on("moveend", schedule);
        map.on("zoomend", schedule);

        return () => {
            alive = false;
            if (timerRef.current) clearTimeout(timerRef.current);
            map.off("moveend", schedule);
            map.off("zoomend", schedule);
        };
    }, [map, commit]);

    return null;
}

/** Leaflet often initializes at 0×0 when parent height is unset (flex/%). */
function MapInvalidateSize() {
    const map = useMap();

    useEffect(() => {
        let alive = true;
        let rafId = 0;
        const el = map.getContainer();

        const run = () => {
            if (!alive || !mapContainerConnected(map)) return;
            try {
                map.invalidateSize({ animate: false });
            } catch {
                /* teardown */
            }
        };

        const scheduleRun = () => {
            if (!alive) return;
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(run);
        };

        run();
        const t = window.setTimeout(scheduleRun, 120);

        map.whenReady(() => {
            if (alive) scheduleRun();
        });

        window.addEventListener("resize", scheduleRun);

        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => scheduleRun());
            ro.observe(el);
        }

        return () => {
            alive = false;
            cancelAnimationFrame(rafId);
            window.clearTimeout(t);
            window.removeEventListener("resize", scheduleRun);
            ro?.disconnect();
        };
    }, [map]);

    return null;
}

function BangladeshInitialBounds() {
    const map = useMap();

    useEffect(() => {
        let alive = true;
        let rafId = 0;

        const fit = () => {
            if (!alive || !mapContainerConnected(map)) return;
            try {
                map.fitBounds(BD_BOUNDS, { padding: [20, 20], animate: false });
            } catch {
                /* teardown */
            }
        };

        map.whenReady(() => {
            if (!alive) return;
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(fit);
        });

        return () => {
            alive = false;
            cancelAnimationFrame(rafId);
        };
    }, [map]);

    return null;
}

function ClusterSiteMarkers({ sites, searchQuery = "" }) {
    const map = useMap();
    const didFitRef = useRef(false);

    useEffect(() => {
        didFitRef.current = false;
    }, [searchQuery]);

    useEffect(() => {
        if (!sites?.length) return undefined;

        const group = USE_MAP_MARKER_CLUSTER
            ? L.markerClusterGroup({
                  showCoverageOnHover: false,
                  maxClusterRadius: 52,
                  spiderfyOnMaxZoom: true,
                  chunkedLoading: true,
              })
            : L.layerGroup();

        sites.forEach((site) => {
            const { lat, lng } = site;
            const sidRaw = site.siteCode != null && String(site.siteCode).trim() !== ""
                ? String(site.siteCode).trim()
                : String(site.id ?? "");
            const sidHtml = escapeHtml(sidRaw.length > 18 ? `${sidRaw.slice(0, 18)}…` : sidRaw);
            const m = L.marker([lat, lng], {
                icon: L.divIcon({
                    className: "map-site-marker map-site-marker--glow",
                    html:
                        `<div class="map-site-marker-inner">` +
                        `<span class="map-site-marker-dot" style="--dot:${site.clusterColor};"></span>` +
                        `<span class="map-site-marker__id">${sidHtml}</span>` +
                        `</div>`,
                    iconSize: [118, 28],
                    iconAnchor: [10, 22],
                }),
            });

            m.bindPopup(buildSitePopupHtml(site), {
                maxWidth: 460,
                minWidth: 300,
                className: "map-popup-site",
            });

            group.addLayer(m);
        });

        map.addLayer(group);

        try {
            if (!didFitRef.current && mapContainerConnected(map)) {
                const valid = sites.filter(
                    (s) => !Number.isNaN(s.lat) && !Number.isNaN(s.lng)
                );
                if (valid.length) {
                    const bounds = L.latLngBounds(valid.map((s) => [s.lat, s.lng]));
                    if (bounds.isValid()) {
                        const searching = Boolean(searchQuery.trim());
                        if (searching) {
                            if (valid.length === 1) {
                                map.flyTo([valid[0].lat, valid[0].lng], Math.max(map.getZoom(), 15), {
                                    duration: 0.45,
                                });
                            } else {
                                map.fitBounds(bounds.pad(0.12));
                            }
                        } else {
                            map.fitBounds(bounds.pad(0.08));
                        }
                        didFitRef.current = true;
                    }
                }
            }
        } catch {
            /* ignore */
        }

        return () => {
            try {
                if (map.hasLayer(group)) {
                    group.clearLayers();
                    map.removeLayer(group);
                }
            } catch {
                /* teardown */
            }
        };
    }, [map, sites, searchQuery]);

    return null;
}

function parseCoord(val) {
    if (val == null || val === "") return NaN;
    if (typeof val === "number") return val;
    const n = parseFloat(String(val).trim().replace(",", "."));
    return Number.isFinite(n) ? n : NaN;
}

function unwrapSiteRow(raw, index) {
    if (raw == null || typeof raw !== "object") return null;
    if (raw.attributes && typeof raw.attributes === "object") {
        return { ...raw.attributes, ...raw };
    }
    return raw;
}

function normalizeSites(rawList) {
    const palette = [
        "#ea580c",
        "#2563eb",
        "#16a34a",
        "#9333ea",
        "#0891b2",
        "#db2777",
        "#ca8a04",
    ];
    const clusterColors = {};
    let ci = 0;

    return rawList
        .map((raw, index) => {
            const row = unwrapSiteRow(raw, index);
            if (!row) return null;

            const nested = row.coordinates;
            const nestedLat =
                nested && typeof nested === "object"
                    ? nested.lat ?? nested.latitude
                    : undefined;
            const nestedLng =
                nested && typeof nested === "object"
                    ? nested.lng ??
                      nested.lang ??
                      nested.longitude ??
                      nested.lon
                    : undefined;

            const lat = parseCoord(
                row.latitude ??
                    row.lat ??
                    nestedLat ??
                    row.site_lat ??
                    row.latitute ??
                    row.site?.lat
            );
            const lng = parseCoord(
                row.longitude ??
                    row.lng ??
                    row.lang ??
                    nestedLng ??
                    row.lon ??
                    row.site_lng ??
                    row.long ??
                    row.site?.lang
            );

            const id =
                row.rms_station_id ??
                row.primary_site_id ??
                row.id ??
                row.site_primary_id ??
                row.site_id ??
                `idx-${index}`;
            const alarmSiteId =
                row.primary_site_id ?? row.site_master_id ?? row.site_id ?? row.id ?? id;
            const siteCodeRaw = row.site_id ?? row.customer_site_id ?? row.site_code ?? row.code;
            const siteCode =
                siteCodeRaw != null && String(siteCodeRaw).trim() !== ""
                    ? String(siteCodeRaw).trim()
                    : "";
            const name =
                row.site_name ??
                row.name ??
                (siteCode || `Site ${id}`);
            const clusterKey =
                row.cluster_id ??
                row.cluster?.id ??
                row.cluster_name ??
                row.zone_id ??
                "default";
            const clusterLabel =
                row.cluster_name ??
                row.cluster?.name ??
                row.zone_name ??
                (clusterKey !== "default" ? String(clusterKey) : "");
            const zoneLabel =
                row.zone_name ?? row.zone?.name ?? "";
            const popupAddress = row.address ?? "";
            const alarmTenants = Array.isArray(row.alarm_tenants) ? row.alarm_tenants : [];

            if (clusterColors[clusterKey] === undefined) {
                clusterColors[clusterKey] = palette[ci % palette.length];
                ci += 1;
            }

            return {
                id,
                alarmSiteId,
                siteCode,
                name,
                lat,
                lng,
                clusterKey,
                clusterLabel,
                zoneLabel,
                popupAddress,
                alarmTenants,
                clusterColor: clusterColors[clusterKey],
            };
        })
        .filter(
            (s) =>
                s != null &&
                s.id != null &&
                s.id !== "" &&
                Number.isFinite(s.lat) &&
                Number.isFinite(s.lng) &&
                Math.abs(s.lat) <= 90 &&
                Math.abs(s.lng) <= 180
        );
}

export default function MapIndex() {
    const [sites, setSites] = useState([]);
    const [totalSites, setTotalSites] = useState(0);
    const [clusterTitle, setClusterTitle] = useState("—");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [boundsQuery, setBoundsQuery] = useState(null);
    const [siteSearchDraft, setSiteSearchDraft] = useState("");
    const [siteSearchQ, setSiteSearchQ] = useState("");

    const lastCommittedBoundsRef = useRef(null);
    const sitesFingerprintRef = useRef(null);

    const onBoundsCommit = useCallback((b) => {
        const next = {
            north: b.north,
            south: b.south,
            east: b.east,
            west: b.west,
        };
        if (boundsNearlyEqual(lastCommittedBoundsRef.current, next)) {
            return;
        }
        lastCommittedBoundsRef.current = next;
        setBoundsQuery(next);
    }, []);

    useEffect(() => {
        const id = window.setTimeout(() => setSiteSearchQ(siteSearchDraft.trim()), 420);
        return () => window.clearTimeout(id);
    }, [siteSearchDraft]);

    useEffect(() => {
        sitesFingerprintRef.current = null;
    }, [siteSearchQ]);

    useEffect(() => {
        if (!boundsQuery) return undefined;

        const controller = new AbortController();

        const params = new URLSearchParams({
            north: String(boundsQuery.north),
            south: String(boundsQuery.south),
            east: String(boundsQuery.east),
            west: String(boundsQuery.west),
            q: siteSearchQ,
        });

        setLoading(true);
        setError(null);

        API.get(`/v1/oauth/map/ajax_sites?${params.toString()}`, {
            signal: controller.signal,
        })
            .then((res) => {
                const body = res.data;
                const arr = extractSiteList(body);
                const list = Array.isArray(arr) ? arr : [];
                const normalized = normalizeSites(list);

                const fp = sitesListFingerprint(normalized);
                if (sitesFingerprintRef.current !== fp) {
                    sitesFingerprintRef.current = fp;
                    setSites(normalized);
                }

                const apiCluster =
                    body?.cluster_name ??
                    body?.data?.cluster_name ??
                    body?.meta?.cluster_name;
                setClusterTitle(
                    apiCluster && String(apiCluster).trim()
                        ? String(apiCluster).trim()
                        : deriveClusterTitle(normalized)
                );

                // Badge must match markers — API `total` can diverge if rows fail parsing.
                setTotalSites(normalized.length);
            })
            .catch((e) => {
                if (axios.isCancel(e) || e?.code === "ERR_CANCELED") return;
                sitesFingerprintRef.current = null;
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        "Failed to load sites"
                );
                setSites([]);
                setTotalSites(0);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [boundsQuery, siteSearchQ]);

    const routeSegments = useMemo(() => buildRouteSegments(sites), [sites]);

    return (
        <div className="live-map-shell px-4">
            <header className="live-map-header mt-4">
                <div>
                    <h1 className="live-map-title">Live monitoring map</h1>
                    <p className="live-map-sub">Pan or zoom to load sites in view</p>
                </div>
                <span className="live-map-badge">Live</span>
            </header>

            <div className="live-map-frame">
                {error && (
                    <div className="live-map-alert alert alert-danger py-2 px-3 mb-0 small">
                        {error}
                    </div>
                )}

                <div className="live-map-float-stack live-map-float-stack--tr">
                    <div className="live-map-float live-map-float--panel">
                        <span className="live-map-float-label">Cluster</span>
                        <span className="live-map-float-value">{clusterTitle}</span>
                    </div>
                    <div className="live-map-float live-map-float--panel">
                        <label className="live-map-float-label" htmlFor="live-map-site-search">
                            Site code
                        </label>
                        <input
                            id="live-map-site-search"
                            type="search"
                            className="form-control form-control-sm live-map-search-input"
                            placeholder="Site ID / customer ID / name…"
                            value={siteSearchDraft}
                            onChange={(e) => setSiteSearchDraft(e.target.value)}
                            autoComplete="off"
                            aria-label="Search sites by code"
                        />
                        {siteSearchDraft ? (
                            <button
                                type="button"
                                className="btn btn-link btn-sm live-map-search-clear p-0 mt-1"
                                onClick={() => setSiteSearchDraft("")}
                            >
                                Clear
                            </button>
                        ) : null}
                    </div>
                </div>
                <div className="live-map-float live-map-float--br">
                    <span className="live-map-float-label">Total sites</span>
                    <span className="live-map-float-value">{totalSites}</span>
                </div>

                {loading && (
                    <div className="live-map-loading">
                        <span className="spinner-border spinner-border-sm text-primary me-2" />
                        Updating…
                    </div>
                )}

                {!loading && !error && sites.length === 0 && (
                    <div className="live-map-empty">No sites in this area.</div>
                )}

                <MapContainer
                    center={BD_CENTER}
                    zoom={9}
                    maxBounds={BD_BOUNDS}
                    maxBoundsViscosity={0.85}
                    className="live-map-leaflet"
                    scrollWheelZoom
                    style={{ height: "100%", width: "100%", minHeight: 320 }}
                >
                    <TileLayer attribution="&copy; Ajra" url={MAP_TILE} />
                    {routeSegments.map(({ a, b }) => (
                        <Polyline
                            key={`${String(a.id)}-${String(b.id)}`}
                            positions={[
                                [a.lat, a.lng],
                                [b.lat, b.lng],
                            ]}
                            pathOptions={ROUTE_LINE}
                        />
                    ))}
                    <MapInvalidateSize />
                    <BangladeshInitialBounds />
                    <MapBoundsListener onBoundsCommit={onBoundsCommit} />
                    {sites.length > 0 ? (
                        <ClusterSiteMarkers sites={sites} searchQuery={siteSearchQ} />
                    ) : null}
                </MapContainer>
            </div>

            <style>{`
        .live-map-shell {
          display: flex;
          flex-direction: column;
          gap: 12px;
          height: calc(100vh - 120px);
          min-height: 480px;
        }
        .live-map-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }
        .live-map-title {
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: #0f172a;
          margin: 0;
        }
        .live-map-sub {
          margin: 4px 0 0;
          font-size: 0.875rem;
          color: #64748b;
        }
        .live-map-badge {
          flex-shrink: 0;
          padding: 6px 14px;
          border-radius: 999px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #15803d;
          border: 1px solid #22c55e;
          background: rgba(34, 197, 94, 0.08);
        }
        .live-map-frame {
          position: relative;
          flex: 1 1 auto;
          min-height: 360px;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          box-shadow: 0 12px 40px rgba(15, 23, 42, 0.06);
          background: #f8fafc;
        }
        .live-map-leaflet,
        .live-map-frame .leaflet-container {
          height: 100%;
          width: 100%;
        }
        .live-map-leaflet {
          z-index: 1;
        }
        .live-map-float {
          position: absolute;
          z-index: 40;
          padding: 12px 16px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(14, 165, 233, 0.22);
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(10px);
          min-width: 140px;
        }
        .live-map-float-stack--tr {
          position: absolute;
          top: 16px;
          right: 16px;
          left: auto;
          z-index: 40;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
          max-width: min(300px, calc(100% - 100px));
          pointer-events: none;
        }
        .live-map-float-stack--tr .live-map-float--panel {
          pointer-events: auto;
          position: relative;
          top: auto;
          left: auto;
          right: auto;
          min-width: 220px;
        }
        .live-map-search-input {
          margin-top: 6px;
        }
        .live-map-search-clear {
          font-size: 0.75rem;
          text-decoration: none;
        }
        .live-map-float--br {
          bottom: 16px;
          right: 16px;
        }
        .live-map-float-label {
          display: block;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #0284c7;
          margin-bottom: 4px;
        }
        .live-map-float-value {
          display: block;
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.2;
        }
        .live-map-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 45;
          display: flex;
          align-items: center;
          padding: 10px 16px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid #e2e8f0;
          font-size: 0.85rem;
          color: #475569;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
        }
        .live-map-empty {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 35;
          font-size: 0.9rem;
          color: #64748b;
          pointer-events: none;
        }
        .live-map-alert {
          position: absolute;
          top: 72px;
          left: 16px;
          right: 16px;
          z-index: 45;
        }
        .map-site-marker {
          background: transparent !important;
          border: none !important;
        }
        .map-site-marker-inner {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 5px;
          height: 28px;
          pointer-events: none;
        }
        .map-site-marker-dot {
          flex-shrink: 0;
          display: block;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--dot, #ea580c);
          border: 2px solid #fff;
          box-shadow:
            0 0 0 2px rgba(234, 88, 12, 0.35),
            0 0 14px rgba(234, 88, 12, 0.55);
        }
        .map-site-marker__id {
          font-size: 10px;
          font-weight: 700;
          line-height: 1.1;
          color: #0f172a;
          letter-spacing: 0.01em;
          white-space: nowrap;
          max-width: 88px;
          overflow: hidden;
          text-overflow: ellipsis;
          text-shadow:
            0 0 3px #fff,
            0 0 6px #fff,
            0 0 10px rgba(255, 255, 255, 0.95);
        }
        .marker-cluster-small,
        .marker-cluster-medium,
        .marker-cluster-large {
          background: rgba(14, 165, 233, 0.2) !important;
          border: 2px solid rgba(14, 165, 233, 0.55) !important;
          color: #0f172a !important;
        }
        .marker-cluster-small div,
        .marker-cluster-medium div,
        .marker-cluster-large div {
          background: rgba(255, 255, 255, 0.95) !important;
          color: #0f172a !important;
        }
          .leaflet-bottom .leaflet-right{
          display:none;
          }
        .leaflet-popup.map-popup-site .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 8px;
          border: 2px solid #2563eb;
          overflow: hidden;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.18);
        }
        .leaflet-popup.map-popup-site .leaflet-popup-content {
          margin: 0;
          min-width: 400px;
          width: auto !important;
        }
        .leaflet-popup.map-popup-site a.leaflet-popup-close-button {
          color: #e2e8f0;
          font-weight: 700;
          padding: 10px 12px 0 0;
          width: 28px;
          height: 28px;
        }
        .leaflet-popup.map-popup-site a.leaflet-popup-close-button:hover {
          color: #fff;
        }
        .map-popup-custom {
          font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
          font-size: 12px;
          color: #0f172a;
        }
        .map-popup-outer {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        .map-popup-title {
          background: #2563eb;
          color: #fff;
          font-weight: 700;
          text-align: left;
          padding: 10px 36px 10px 12px;
          font-size: 13px;
          letter-spacing: 0.02em;
        }
        .map-popup-k {
          border: 1px solid #2563eb;
          padding: 8px 10px;
          font-weight: 600;
          width: 92px;
          vertical-align: top;
          background: #f8fafc;
        }
        .map-popup-v {
          border: 1px solid #2563eb;
          padding: 8px 10px;
          vertical-align: top;
          word-break: break-word;
        }
        .map-popup-banner {
          border: 1px solid #2563eb;
          padding: 8px;
          text-align: center;
          font-weight: 700;
          background: #eef2f7;
          color: #334155;
        }
        .map-popup-alarm-wrap {
          padding: 0 !important;
          border: 1px solid #2563eb;
          border-top: none;
          vertical-align: top;
        }
        .map-popup-inner {
          width: 100%;
          border-collapse: collapse;
        }
        .map-popup-inner-th {
          border: 1px solid #2563eb;
          padding: 6px 8px;
          background: #dbeafe;
          font-weight: 700;
          font-size: 11px;
          text-align: center;
        }
        .map-popup-inner-corner {
          text-align: left;
        }
        .map-popup-inner-label {
          border: 1px solid #2563eb;
          padding: 6px 8px;
          font-weight: 600;
          background: #fafafa;
        }
        .map-popup-inner-td {
          border: 1px solid #2563eb;
          padding: 6px 8px;
          text-align: center;
          vertical-align: middle;
        }
        .map-popup-inner-empty {
          padding: 12px;
          text-align: center;
          color: #64748b;
        }
        .map-popup-status {
          display: inline-block;
          width: 22px;
          height: 22px;
          border-radius: 4px;
          vertical-align: middle;
          box-shadow:
            inset 0 -3px 6px rgba(0, 0, 0, 0.2),
            inset 0 2px 4px rgba(255, 255, 255, 0.35);
        }
        .map-popup-status--ok {
          background: linear-gradient(165deg, #86efac 0%, #22c55e 55%, #15803d 100%);
        }
        .map-popup-status--warn {
          background: linear-gradient(165deg, #fde047 0%, #f59e0b 55%, #b45309 100%);
        }
      `}</style>
        </div>
    );
}
